
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import type { AxiosError } from 'axios';

import axios from 'axios';

import {
  defineComponent,
  defineAsyncComponent,
  ref,
  reactive,
  computed,
  onMounted,
} from 'vue';

import { useI18n } from 'vue-i18n';

import { useToast } from 'primevue/usetoast';

import type {
  GenericObject,
  ErrorsMap,
  Option,
  Projects,
  Payment,
  Payments,
} from '@/types';

import {
  ENTRIES,
  PAYMENT_METHOD_MANUALLY,
  PAYMENT_METHOD_STRIPE,
  PAYMENT_METHODS,
  PAYMENT_METHODS_MAP,
  PAYMENT_STATUS_NEW,
  PAYMENT_STATUS_COMPLETED,
  PAYMENT_STATUSES,
  PAYMENT_STATUSES_MAP,
  PAYMENT_AMOUNT_MIN,
  PAYMENT_AMOUNT_MIN_STAFF,
  PAYMENT_METHOD_CAPITALIST,
} from '@/libs/consts';

import useUser from '@/composable/useUser';

import useEntriesNext from '@/composable/useEntries@next';

import useUrlState from '@/composable/useUrlState';

export default defineComponent({
  components: {
    UserColumn: defineAsyncComponent(() => import('@/components/UserColumn.vue')),
    StatisticsByPayments: defineAsyncComponent(() => import('@/components/Widgets/StatisticsByPayments.vue')),
    BalancesSum: defineAsyncComponent(() => import('@/components/Widgets/BalancesSum.vue')),
  },
  setup() {
    const i18n = useI18n();

    const toast = useToast();

    const { activeUser: user } = useUser();

    const {
      entries: projects,
      entriesLoading: projectsLoading,
      fetchEntries: fetchProjects,
    } = useEntriesNext<Projects>('/api/project/getProjects', ENTRIES);

    const {
      entries: payments,
      fetchEntries: fetchPayments,
    } = useEntriesNext<Payments>('/api/payment/getPaymentsPaginator', ENTRIES);

    const paymentsFilters = useUrlState('filters', {
      project_id: null,
      user_id: null,
      method: null,
      status: null,
    });

    const usersParams = computed(() => ({
      include: 'project',
      'filter[project_id]': user.isSuperAdmin() ? paymentsFilters.project_id : undefined,
    }));

    const paymentLoading = ref<boolean>(false);

    const paymentAmountMin = computed((): number => (user.isAffiliate() ? PAYMENT_AMOUNT_MIN : PAYMENT_AMOUNT_MIN_STAFF));

    const paymentForm = reactive<GenericObject>({
      method: null,
      amount: paymentAmountMin.value,
      promocode: null,
      back_url: window.location.href,
    });

    const paymentFormErrors = ref<ErrorsMap>({});

    // eslint-disable-next-line arrow-body-style
    const filteredPaymentMethods = computed((): Option[] => {
      return PAYMENT_METHODS
        .filter((option: Option) => option.value !== PAYMENT_METHOD_MANUALLY)
        .map((option: Option) => ({
          value: option.value,
          translate_key: option.translate_key,
          disabled: option.disabled
            || (option.value === PAYMENT_METHOD_STRIPE && !user.enable_pay_by_stripe)
            || (option.value === PAYMENT_METHOD_CAPITALIST && !user.enable_pay_by_capitalist),
        }));
    });

    const fetchPaymentsWrapper = async (): Promise<void> => fetchPayments({
      ...paymentsFilters,
    });

    const onProjectChange = (): void => {
      paymentsFilters.user_id = null;
    };

    const addPayment = async (): Promise<void> => {
      paymentLoading.value = true;

      paymentFormErrors.value = {};

      try {
        const response = await axios.post('/api/payment/addPaymentViaPaymentService', paymentForm);

        const payment = response.data as Payment;

        payments.data.unshift(payment);

        if (payment.payment_url) {
          window.location.href = payment.payment_url;
        }
      } catch (error: unknown | AxiosError) {
        // В случае ошибки валидации API возвращает ответ с кодом 422
        if (axios.isAxiosError(error) && error.response?.status === 422) {
          paymentFormErrors.value = error.response.data;

          toast.add({
            severity: 'error',
            summary: i18n.t('error'),
            detail: i18n.t('validation_error'),
            life: 2000,
          });
        } else {
          toast.add({
            severity: 'error',
            summary: i18n.t('error'),
            detail: i18n.t('unknown_error'),
            life: 2000,
          });
        }
      }

      paymentLoading.value = false;
    };

    onMounted(async (): Promise<void> => {
      if (user.isSuperAdmin()) {
        await fetchProjects();
      }
    });

    return {
      PAYMENT_METHODS_MAP,
      PAYMENT_STATUS_NEW,
      PAYMENT_STATUS_COMPLETED,
      PAYMENT_STATUSES,
      PAYMENT_STATUSES_MAP,
      user,
      projects,
      projectsLoading,
      payments,
      paymentsFilters,
      usersParams,
      paymentLoading,
      paymentAmountMin,
      paymentForm,
      paymentFormErrors,
      filteredPaymentMethods,
      fetchPaymentsWrapper,
      onProjectChange,
      addPayment,
    };
  },
});
